/**
 * Minimal, dependency-free JSON line diff for the Duplicate Store troubleshooting tool.
 * Pretty-prints two JSON values with stable (sorted) key ordering and classifies each
 * resulting line as unchanged / added / removed via a longest-common-subsequence pass,
 * so the UI can render a GitHub-style before/after diff.
 */

export type DiffLineType = 'unchanged' | 'added' | 'removed';

export interface IDiffLine {
    type: DiffLineType;
    text: string;
    /** 1-based line number in the "before" document, or null for added lines. */
    beforeLineNo: number | null;
    /** 1-based line number in the "after" document, or null for removed lines. */
    afterLineNo: number | null;
}

/**
 * Serializes a value to pretty JSON with object keys sorted recursively, so two logically
 * equal objects with different key orders produce identical text (and don't diff as changed).
 */
export function stableStringify(value: unknown): string {
    const normalize = (input: unknown): unknown => {
        if (Array.isArray(input)) {
            return input.map(normalize);
        }
        if (input && typeof input === 'object') {
            return Object.keys(input as Record<string, unknown>)
                .sort()
                .reduce<Record<string, unknown>>((acc, key) => {
                    acc[key] = normalize((input as Record<string, unknown>)[key]);
                    return acc;
                }, {});
        }
        return input;
    };

    if (value === null || value === undefined) {
        return '';
    }
    return JSON.stringify(normalize(value), null, 2);
}

const toLines = (text: string): string[] => (text === '' ? [] : text.split('\n'));

/**
 * Classic LCS line diff producing a unified sequence of removed/added/unchanged lines.
 */
export function diffLines(beforeText: string, afterText: string): IDiffLine[] {
    const before = toLines(beforeText);
    const after = toLines(afterText);
    const n = before.length;
    const m = after.length;

    // lcs[i][j] = length of LCS of before[i..] and after[j..]
    const lcs: number[][] = Array.from({ length: n + 1 }, () => new Array<number>(m + 1).fill(0));
    for (let i = n - 1; i >= 0; i--) {
        for (let j = m - 1; j >= 0; j--) {
            lcs[i][j] = before[i] === after[j]
                ? lcs[i + 1][j + 1] + 1
                : Math.max(lcs[i + 1][j], lcs[i][j + 1]);
        }
    }

    const result: IDiffLine[] = [];
    let i = 0;
    let j = 0;
    let beforeNo = 1;
    let afterNo = 1;

    while (i < n && j < m) {
        if (before[i] === after[j]) {
            result.push({ type: 'unchanged', text: before[i], beforeLineNo: beforeNo++, afterLineNo: afterNo++ });
            i++;
            j++;
        } else if (lcs[i + 1][j] >= lcs[i][j + 1]) {
            result.push({ type: 'removed', text: before[i], beforeLineNo: beforeNo++, afterLineNo: null });
            i++;
        } else {
            result.push({ type: 'added', text: after[j], beforeLineNo: null, afterLineNo: afterNo++ });
            j++;
        }
    }
    while (i < n) {
        result.push({ type: 'removed', text: before[i++], beforeLineNo: beforeNo++, afterLineNo: null });
    }
    while (j < m) {
        result.push({ type: 'added', text: after[j++], beforeLineNo: null, afterLineNo: afterNo++ });
    }

    return result;
}

/**
 * Computes the diff between two JSON values. When only `after` is present (a created entity),
 * every line is rendered as an addition; when only `before` is present, as a removal.
 */
export function computeJsonDiff(before: unknown, after: unknown): IDiffLine[] {
    return diffLines(stableStringify(before), stableStringify(after));
}
